import { lazy, Suspense, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuthorization } from '@hooks/useAuthorization';
// KAN-1181: admin pages are code-split so the shell + rail load first and each
// admin surface (finance/audit/compliance/etc.) is fetched only when routed to.
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const PendingSellers = lazy(() => import('./components/PendingSellers'));
const ManageUsers = lazy(() => import('./components/ManageUsers'));
const ManageProducts = lazy(() => import('./components/ManageProducts'));
const AdminFinanceExports = lazy(() => import('./components/AdminFinanceExports'));
const AdminPolicies = lazy(() => import('./components/AdminPolicies'));
const AuditCenter = lazy(() => import('./components/AuditCenter'));
const NotificationTemplates = lazy(() => import('./components/NotificationTemplates'));
const WorkflowTasks = lazy(() => import('./components/WorkflowTasks'));
const ComplianceCenter = lazy(() => import('./components/ComplianceCenter'));
import { ToastProvider } from '@/components/feedback/ToastProvider';
import { DialogProvider, SkipLink, LoadingState } from '@/components/ui';
import { ErrorBoundary } from '@components/common/ErrorBoundary';
import { getCustomerPortalUrl } from '@/config/portalUrls';
import { useAuth } from '../context/AuthContext';
import {
  RetailAppShell,
  useRetailNav,
  type RetailNavSection
} from '@portals/shared/components/RetailAppShell';
import OtpSecuritySettings from '@components/security/OtpSecuritySettings';
import OtpVerificationNotice from '@components/security/OtpVerificationNotice';
import './AdminApp.css';

/**
 * KAN-2035 — the admin rail, grouped as the approved mock (Platform / Money / Trust).
 * Every destination the old flat <AdminNavbar> exposed is still here; they are now
 * grouped by intent instead of one 11-link strip, and the shell owns the mobile drawer.
 */
const NAV_SECTIONS: RetailNavSection[] = [
  {
    id: 'platform',
    label: 'Platform',
    items: [
      { to: '/dashboard', label: 'Overview' },
      { to: '/sellers/pending', label: 'Sellers' },
      { to: '/users', label: 'Customers' },
      { to: '/products', label: 'Catalogue policy' }
    ]
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      // Settlements map to dhruvanta-settlement; see the mock's design notes.
      { to: '/finance', label: 'Settlements' },
      { to: '/workflows', label: 'Workflow queue' }
    ]
  },
  {
    id: 'trust',
    label: 'Trust',
    items: [
      { to: '/policies', label: 'Policies' },
      { to: '/compliance', label: 'Compliance' },
      { to: '/audit', label: 'Audit log' },
      { to: '/notifications/templates', label: 'Notification templates' },
      { to: '/security', label: 'Account security' }
    ]
  }
];

function AdminApp() {
  const { isAuthenticated, hasRole } = useAuthorization();
  const { authState, logout } = useAuth();
  const navigate = useNavigate();
  const nav = useRetailNav(NAV_SECTIONS);
  const storeHomeUrl = getCustomerPortalUrl('/');
  const storePortalUrl = getCustomerPortalUrl('/retail');

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const topbarEnd = useMemo(
    () => (
      <>
        <span className="rt-chip">Admin</span>
        <a
          href={storePortalUrl}
          className="rt-btn rt-btn--ghost rt-btn--sm"
          target="_blank"
          rel="noreferrer"
        >
          Store
        </a>
        <button type="button" onClick={handleLogout} className="rt-btn rt-btn--ghost rt-btn--sm">
          Logout
        </button>
        <span className="rt-avatar" aria-hidden="true">
          {(authState.user?.name ?? 'Admin').slice(0, 2).toUpperCase()}
        </span>
      </>
    ),
    [authState.user?.name, handleLogout, storePortalUrl]
  );

  if (!isAuthenticated || !hasRole(['SUPER_ADMIN'])) {
    return (
      <div className="admin-access-denied">
        <h1>Access Denied</h1>
        <p>You don't have permission to access the admin panel.</p>
        <a href={storeHomeUrl}>Back to Store</a>
      </div>
    );
  }

  return (
    <ToastProvider>
      <DialogProvider>
        <RetailAppShell
          role="Admin"
          nav={nav}
          navAriaLabel="Admin console"
          searchPlaceholder="Search anything…"
          topbarEnd={topbarEnd}
        >
          <SkipLink targetId="admin-main" />
          <div id="admin-main" className="admin-content rt-content" tabIndex={-1}>
            <OtpVerificationNotice portal="admin" profilePath="/security" className="admin-otp-notice" />
            {/* KAN-1180: scope the boundary to the routed content so a single page throw
                degrades to the fallback WITHOUT white-screening the whole admin portal
                (the rail stays interactive for recovery). */}
            <ErrorBoundary>
              <Suspense fallback={<LoadingState label="Loading…" />}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<AdminDashboard />} />
                <Route path="/sellers/pending" element={<PendingSellers />} />
                <Route path="/users" element={<ManageUsers />} />
                {/* KAN-788/789/790/792 — real admin-portal backends now wired (EPIC KAN-769). */}
                <Route path="/products" element={<ManageProducts />} />
                <Route path="/finance" element={<AdminFinanceExports />} />
                <Route path="/policies" element={<AdminPolicies />} />
                <Route path="/audit" element={<AuditCenter />} />
                {/* KAN-811 — notification-service template admin (GET/POST/PUT /api/v2/notifications/templates). */}
                <Route path="/notifications/templates" element={<NotificationTemplates />} />
                {/* KAN-806 — admin workflow task queue now wired (admin-service AdminWorkflowController
                    proxies workflow-service /v2/workflows/tasks; EPIC KAN-676). */}
                <Route path="/workflows" element={<WorkflowTasks />} />
                {/* KAN-812 (EPIC KAN-676) — admin compliance/DPDP oversight: privacy-request queue +
                    consent ledger (admin-service /api/v2/admin/compliance aggregating auth-service). */}
                <Route path="/compliance" element={<ComplianceCenter />} />
                <Route path="/security" element={<OtpSecuritySettings portal="admin" hideWhenVerified={false} className="admin-otp-settings" />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
          </div>
        </RetailAppShell>
      </DialogProvider>
    </ToastProvider>
  );
}

export default AdminApp;
